import type { Maybe } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { Weekday } from '@chancenwerk/enums/enums.js';
import { ProductLKK } from '@chancenwerk/shared/_cms/products/entities/ProductLKK.js';
import { archiveCourse, Course } from '@chancenwerk/shared/courses/Course.js';
import { CourseAbschluss } from '@chancenwerk/shared/courses/CourseAbschluss.js';
import { CourseChancenschule } from '@chancenwerk/shared/courses/CourseChancenschule.js';
import { CourseDigital } from '@chancenwerk/shared/courses/CourseDigital.js';
import { CourseIntensive } from '@chancenwerk/shared/courses/CourseIntensive.js';
import {
  CourseLernfoerderung,
  CourseLernfoerderungVersion,
  courseMatchesAnyDurchfuehrungsTag,
  courseMatchesDurchfuehrungsTag,
} from '@chancenwerk/shared/courses/CourseLernfoerderung.js';
import { CourseLUS } from '@chancenwerk/shared/courses/CourseLUS.js';
import { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import type { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import { Student } from '@chancenwerk/shared/master-data-management/users/entities/Student.js';
import { TemporaryUser } from '@chancenwerk/shared/master-data-management/users/entities/TemporaryUser.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import { StudentsReport } from '@chancenwerk/shared/StudentsReport.js';
import { html, nothing, type PropertyValueMap } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import './board-component-course-startdate.js';
import type { BoardComponentUserCard } from './board-component-user-card.js';
import { BoardDialogEditCourse, CourseProxy } from './dialogs/board-dialog-edit-course.js';

type CourseUserArray = 'schuKos' | 'usStudents' | 'osStudents' | 'leKos' | 'diKus';
type CourseUserArrayIDs = 'schuKoIDs' | 'usStudentIDs' | 'osStudentIDs' | 'leKoIDs' | 'diKuIDs';
type UserType = 'OS' | 'US' | 'SchuKo' | 'LeKo' | 'DiKu';

const courseSelectorDigital: BaseQLSelectionSet<CourseDigital> = { _class: 1, _id: 1, linkedKPFIDs: 1 };

export interface CourseDetailCardAction {
  icon: string;
  tooltip?: string;
  action: (courses: Course[]) => void;
}

export type UserFilter = (c: Course[], type: UserType, user: ChancenwerkUser) => boolean;

/**
 * A hint rendered on a user card. 'error' (rot) is reserved for people who left - ausgetreten or no
 * longer active. 'info' (blau) marks an assignment that needs a look but is not itself invalid.
 */
export type CardWarning = { text: string; tone: 'error' | 'info' } | null;

/**
 * @customElement board-component-course-detail-card
 */
@customElement('board-component-course-detail-card')
export class BoardComponentCourseDetailCard extends ChemistryLitElement {
  @property({ attribute: false }) public coursesToManage = new RXController(this, [] as Course[]);
  @property({ attribute: false }) public userFilter: Maybe<UserFilter>;
  @property({ attribute: false }) public kpf = new RXController(this, new KPF({}));
  @property({ attribute: false }) public deleteDigital = false;
  @property({ attribute: false }) public actions: Maybe<CourseDetailCardAction[]> = null;
  @property({ attribute: 'hide-delete', type: Boolean }) public hideDelete = false;
  @property({ attribute: 'hide-edit', type: Boolean }) public hideEdit = false;

  @property({ attribute: false }) dropzonesEnabled: UserType[] = ['SchuKo', 'LeKo', 'OS', 'US', 'DiKu'];
  /**
   * Courses of the same KPF that this card does NOT manage. Dropping a user here also unassigns them
   * from these, which is how "Einteilung nach Raum" keeps a child in exactly one room. Left empty for
   * course types where being in several cards at once is legitimate.
   */
  @property({ attribute: false }) public otherCourses: Course[] = [];

  @state() _studentsReports: Maybe<StudentsReport> = null;

  override updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);

    if (changedProperties.has('kpf')) {
      StudentsReport.getStudentsReportForKPF(this.kpf.value._id)(StudentsReport.allFields)
        .then(r => {
          this._studentsReports = r;
        })
        .catch(console.error);
    }
  }

  private _lkkV2() {
    return this.coursesToManage.value.some(c =>
      c instanceof CourseLernfoerderung ? c.version === CourseLernfoerderungVersion.V2 : false,
    );
  }

  private _chunkedByWeekday() {
    return this.coursesToManage.value.some(c => c._class === CourseChancenschule._class);
  }

  private _sanetizeLink(link?: Maybe<string>) {
    if (!link || link === '') {
      return link;
    }
    if (!link.startsWith('http://') && !link.startsWith('https://')) {
      return `https://${link}`;
    }
    return link;
  }

  private _location() {
    if (this.coursesToManage.value.at(0) instanceof CourseAbschluss) {
      return html`<cr-flex horizontal crossaxis-center space="xs">
        <cr-text bold> Schüler Link: </cr-text> ${this.coursesToManage.value.at(0)?.location
          ? html`
              <cr-button
                mode="icon"
                icon="link"
                href="${this._sanetizeLink(this.coursesToManage.value.at(0)?.location)}"
              ></cr-button>
            `
          : html` <cr-text bold ${css({ color: this.colors.tone.error })}> ? </cr-text> `}

        <cr-text bold> DiKu Link: </cr-text> ${this.coursesToManage.value.at(0)?.diKuLink
          ? html`
              <cr-button
                mode="icon"
                icon="link"
                href="${this._sanetizeLink(this.coursesToManage.value.at(0)?.diKuLink)}"
              ></cr-button>
            `
          : html` <cr-text bold ${css({ color: this.colors.tone.error })}> ? </cr-text> `}
      </cr-flex>`;
    }

    return html`<cr-text bold> Raum: </cr-text> ${this.coursesToManage.value.at(0)?.location
        ? html` <cr-text>${this.coursesToManage.value.at(0)?.location}</cr-text> `
        : html` <cr-text bold ${css({ color: this.colors.tone.error })}> ? </cr-text> `}`;
  }

  private _locationOrWeekday() {
    if (this._lkkV2()) {
      return nothing;
    }

    return html`
      <cr-flex space="sm" horizontal>
        ${this._chunkedByWeekday() && this.coursesToManage.value?.at(0)?.weekday
          ? html`<cr-text bold> Wochentag: </cr-text>
              <cr-text> ${this.coursesToManage.value?.at(0)?.weekday} </cr-text>`
          : this._location()}
      </cr-flex>
    `;
  }

  private _startEndDate() {
    return html`
      <board-component-course-startdate
        .plannedStartdate=${this.coursesToManage.value.at(0)?.plannedStartDate}
        .actualStartdate=${this.coursesToManage.value.at(0)?.startDate}
      ></board-component-course-startdate>
      ${this.coursesToManage.value.at(0) instanceof CourseAbschluss
        ? html`<cr-flex
            horizontal
            space="sm"
            padding="sm"
            ${css({ backgroundColor: this.colors.neutral.shade(50), borderRadius: this.spacing.sm })}
          >
            <cr-text bold> Enddatum:</cr-text>
            <cr-text>
              ${(this.coursesToManage.value.at(0) as CourseAbschluss).endDate?.toFormat('dd.MM.yyyy')}
            </cr-text>
          </cr-flex>`
        : nothing}
    `;
  }

  private _actions() {
    return html`
      ${this.actions?.map(a => {
        return html`<cr-icon
          type="material-rounded"
          ${css({ cursor: 'pointer', color: this.colors.neutral.shade(700) })}
          @click=${() => a.action(this.coursesToManage.value)}
        >
          ${a.tooltip ? html`<cr-tooltip><cr-text>${a.tooltip}</cr-text></cr-tooltip>` : nothing} ${a.icon}
        </cr-icon>`;
      })}
      ${this._durchfuehrungsTagWarning()}
      ${!this.hideEdit
        ? permissionGuard(
            ChancenPermission.board.EDIT_COURSE,
            () =>
              html`<cr-icon
                type="material-rounded"
                ${css({ justifySelf: 'end', color: this.colors.neutral.shade(700), cursor: 'pointer' })}
                @click=${async () => {
                  await BoardDialogEditCourse.showPopup({
                    props: {
                      courseProxy: new CourseProxy({
                        location: this.coursesToManage.value[0]?.location,
                        courses: this.coursesToManage.value,
                      }),
                      kpf: this.kpf.value,
                      courseType: this.coursesToManage.value[0]?._class,
                      new: false,
                    },
                  });
                  // Der Dialog bearbeitet genau die Course-Objekte, aus denen Karte und Seite rendern.
                  // Ein Feld an einem Objekt im Array zu ändern macht keine reaktive Property dirty,
                  // also bleiben ohne diesen Anstoß Wochentag/Uhrzeit und der Durchführungstag-Hinweis
                  // stehen, bis jemand die Seite neu lädt. Die Seite braucht es ebenfalls: geänderte
                  // Kurszeiten verschieben, welche Kinder im Nutzerpool auftauchen.
                  this.requestUpdate();
                  this.dispatchEvent(new CustomEvent('course-edited'));
                }}
              >
                settings
              </cr-icon>`,
            { forbiddenTemplate: html``, context: ANY_CONTEXT },
          )
        : nothing}
      ${!this.hideDelete
        ? html`<cr-icon
            type="material-rounded"
            ${css({ justifySelf: 'end', color: this.colors.tone.error, cursor: 'pointer' })}
            @click=${async () => {
              const warning = html`<cr-h4>Warnung!</cr-h4> Das Archivieren eines Kurses entfernt die meisten Referenzen
                auf diesen Kurs! Falls du z.B. nur den Namen eines Raumes ändern willst, nutze die "Bearbeiten" Funktion
                (Rädchen neben dem Mülleimer)!`;
              if (
                await ChromiumDialog.confirm(
                  html`<cr-flex ${css({ maxWidth: '500px', paddingBottom: this.spacing.lg })} space="md">
                    ${warning}
                    <cr-text>
                      ${this.coursesToManage.value.length > 1
                        ? `${this.coursesToManage.value.length} Kurse wirklich archivieren?`
                        : 'Den Kurs wirklich archivieren?'}
                    </cr-text>
                  </cr-flex>`,
                  { highlightDecline: true, acceptText: 'Jetzt archivieren!', declineText: 'Abbrechen' },
                )
              ) {
                await ChromiumDialog.waitFor(this._archiveCourse());
              }
            }}
          >
            delete
          </cr-icon>`
        : nothing}
    `;
  }

  /**
   * Courses whose weekday/time matches none of the KPF's Durchführungstage. Such a course matches no
   * student day wish either, so everyone in it is shown as misassigned and disappears from the pool -
   * the cause sits on the course, not on the children, which is why we flag it here.
   */
  private _coursesWithoutDurchfuehrungsTag(): Course[] {
    const days = (this.kpf.value?.produkt as Maybe<ProductLKK>)?.durchfuehrungsTage ?? [];
    if (!days.length) return [];
    return this.coursesToManage.value.filter(
      c => (c instanceof CourseLernfoerderung || c instanceof CourseLUS) && !courseMatchesAnyDurchfuehrungsTag(c, days),
    );
  }

  private _durchfuehrungsTagWarning() {
    const mismatched = this._coursesWithoutDurchfuehrungsTag();
    if (!mismatched.length) return nothing;

    const slots = mismatched
      .map(
        c =>
          `${c.weekday ?? 'Tag ?'} ${c.startTime?.toFormat('HH:mm') ?? '?'} - ${
            c.endTime?.toFormat('HH:mm') ?? '?'
          }`,
      )
      .join(', ');

    return html`<cr-icon
      type="material-rounded"
      ${css({ justifySelf: 'end', color: this.colors.tone.warning, cursor: 'help' })}
    >
      <cr-tooltip>
        <cr-text>
          Kein passender Durchführungstag: ${slots}. Kinder in diesem Kurs werden dadurch als falsch
          eingeteilt angezeigt und fehlen im Nutzerpool - bitte über das Zahnrad korrigieren.
        </cr-text>
      </cr-tooltip>
      warning
    </cr-icon>`;
  }

  private _courseLeftDetails(c: Course) {
    const renderables: Renderable[] = [];

    // School
    if (c.kpfID != this.kpf.value._id) {
      renderables.push(
        html`<cr-flex horizontal space="sm" crossaxis-center>
          <cr-icon>school</cr-icon><cr-text>${c.kpf?.school?.name}</cr-text>
        </cr-flex>`,
      );
    }

    // Weekday or Room
    if (this._chunkedByWeekday()) {
      renderables.push(html`<cr-text> Raum ${c.location ?? '?'}</cr-text>`);
    } else {
      if (c.weekday) {
        if (this._lkkV2()) {
          renderables.push(html`<cr-text bold> ${c.weekday} </cr-text>`);
        } else {
          renderables.push(html`<cr-text> ${c.weekday} </cr-text>`);
        }
      } else {
        renderables.push(html`<cr-text bold ${css({ color: this.colors.tone.error })}> Tag ? </cr-text>`);
      }
    }

    // Time
    if (c.startTime && c.endTime) {
      renderables.push(html`<cr-text> ${c.startTime.toFormat('HH:mm')} - ${c.endTime.toFormat('HH:mm')} </cr-text>`);
    } else {
      renderables.push(html`<cr-text bold ${css({ color: this.colors.tone.error })}> Uhrzeit ? </cr-text>`);
    }

    // Subject
    if (c instanceof CourseIntensive && (c.subjectOne !== '' || c.subjectTwo !== '')) {
      renderables.push(html`<cr-text bold> ${c.subjectOne} ${c.subjectTwo} </cr-text>`);
    }

    // CourseAbschluss
    if (c instanceof CourseAbschluss) {
      renderables.push(
        html`<cr-text bold>
          ${c.subject} <br />
          ${c.type}
        </cr-text>`,
      );
    }

    // CourseDigital
    if (c instanceof CourseDigital) {
      renderables.push(html`<cr-text> ${c.subject ?? ''} </cr-text>`);
      if (c.jahrgangsstufe) {
        renderables.push(html`<cr-text> Jgst. ${c.jahrgangsstufe ?? ''} </cr-text>`);
      }
    }

    return html`${renderables}`;
  }

  override render() {
    if (!this.coursesToManage.value.length) return nothing;

    return html`
      <cr-card space="md" ${css({ borderRadius: this.sizes.borderRadiusSecondary })}>
        <cr-flex space="sm" space-between horizontal crossaxis-center>
          <cr-flex space="md" horizontal crossaxis-center>
            ${this._locationOrWeekday()} ${this._startEndDate()}
          </cr-flex>
          <cr-flex space="sm" horizontal center crossaxis-center> ${this._actions()} </cr-flex>
        </cr-flex>
        <cr-flex space="sm" horizontal>
          <cr-flex space="sm" ${css({ minWidth: '110px' })}>
            <cr-text bold> Wann: </cr-text>
            <cr-flex space="md" vertical>
              ${repeat(
                this.coursesToManage.value.sort((a, b) => {
                  return this._sortAfterWeekday(a.weekday ?? Weekday.MONDAY, b.weekday ?? Weekday.MONDAY);
                }),
                c => html`
                  <cr-flex
                    space="sm"
                    ${css({
                      padding: `${this.spacing.sm} 0 ${this.spacing.sm} ${this.spacing.sm}`,
                      borderLeft: `4px solid ${this.colors.accent}`,
                    })}
                  >
                    ${this._courseLeftDetails(c)}
                  </cr-flex>
                `,
              )}
            </cr-flex>
          </cr-flex>

          <cr-flex horizontal space="md" wrap>
            ${repeat(
              this.dropzonesEnabled,
              s => s,
              dropzone => {
                let users = this._getDeduplicatedUsers(this.coursesToManage.value, this._getArrayForUserType(dropzone));
                if (this.userFilter) {
                  users = users.filter(u => {
                    if (!this.userFilter) {
                      return true;
                    }
                    return this.userFilter(this.coursesToManage.value, dropzone, u.user);
                  });
                }
                users = this._sortUsers(users);

                const coursesMapped = this._getCourseMappedByParticipants(
                  this.coursesToManage.value,
                  this._getIDArrayForUserType(dropzone),
                );

                return this._getDropzone(dropzone, users, coursesMapped);
              },
            )}
          </cr-flex>
        </cr-flex>
      </cr-card>
    `;
  }

  private _getDropzone(
    userType: 'SchuKo' | 'LeKo' | 'OS' | 'US' | 'DiKu',
    users: Array<{ user: ChancenwerkUser; deduped: boolean; fromCourseIndex: number }>,
    coursesMapped: Course[][],
  ) {
    return html`
      <cr-flex space="md" ${css({ minWidth: '300px' })}>
        <cr-flex vertical space="sm">
          ${repeat(coursesMapped, c => {
            const course = c[0];
            if (!course) return nothing;

            if (this._lkkV2()) {
              return html`<cr-flex horizontal space-between ${css({ padding: `0 ${this.spacing.md}` })}>
                <cr-text bold>${this._getNameUserType(userType)} </cr-text>
                <cr-text>
                  ${course[this._getArrayForUserType(userType)].filter(u => !!u || u._class !== TemporaryUser._class)
                    .length}
                </cr-text>
              </cr-flex> `;
            }

            return this._getProgressBar(
              course,
              this._getArrayForUserType(userType),
              this._getMaximumPersonsPerUserType(userType, course),
              this._getNameUserType(userType),
            );
          })}
        </cr-flex>
        <board-component-drag-drop
          flex
          type="${userType}"
          dropzone-accept-selector="board-component-user-card[${userType.toLocaleLowerCase()}][pool]"
          ${css({ minHeight: '15rem', padding: this.spacing.xs })}
          .allow-from="board-component-user-card[${userType.toLocaleLowerCase()}]"
          @dropped="${async (e: CustomEvent) => {
            // `otherCourses` are the courses of this KPF handled by *other* cards. Including them here
            // is what enforces "ein Kind ist nur in einem Raum": the drop pulls the user out of every
            // room but this one. It stays empty where multi-card membership is legitimate.
            const oldCourses = [...e.detail.oldAssignments.value, ...this.otherCourses];
            for (const course of this.coursesToManage.value) {
              if (e.detail.oldAssignments.value.some(c => c._id === course._id)) continue;

              // TODO this should be better handled somewhere else
              if (userType === 'DiKu') {
                if (course instanceof CourseDigital || course instanceof CourseAbschluss) {
                  course.diKuIDs?.push(e.detail.user._id);
                  course.diKus?.push(e.detail.user);
                }
              } else {
                Course.assignUserToCourse(e.detail.user, course, { arrayToAssign: userType, oldCourses });
              }
            }

            const event = new CustomEvent('dropped', { detail: { value: e.detail.user } });
            this.dispatchEvent(event);

            this.requestUpdate();
          }}"
          @dropped-elsewhere=${() => {
            const event = new CustomEvent('dropped-elsewhere', {});
            this.dispatchEvent(event);
            this.requestUpdate();
          }}
          duplicate
          .duplicateFn=${(staticEl: BoardComponentUserCard, movingEl: BoardComponentUserCard) => {
            movingEl.user = staticEl.user;
          }}
        >
          <cr-flex ${css({ gap: '4px' })} vertical>
            ${repeat(
              users,
              userInfo => userInfo.user._id,
              userInfo => {
                const warning = this._warning(userType, userInfo.user);
                return html`
                <board-component-user-card
                  .showCourse=${this._getUserIsNotInAll(userInfo.user._id, this._getIDArrayForUserType(userType))}
                  ?US=${userType === 'US'}
                  ?OS=${userType === 'OS'}
                  ?LeKo=${userType === 'LeKo'}
                  ?SchuKo=${userType === 'SchuKo'}
                  ?DiKu=${userType === 'DiKu'}
                  assigned
                  .user=${userInfo.user}
                  .studentsReport=${this._studentsReports}
                  .oldAssignments=${this.coursesToManage.value.filter(c =>
                    c[this._getIDArrayForUserType(userType)].includes(userInfo.user._id),
                  )}
                  .allCourses=${this._lkkV2() ? this.coursesToManage.value : []}
                  .warning=${warning?.text ?? null}
                  .warningTone=${warning?.tone ?? 'error'}
                ></board-component-user-card>
              `;
              },
            )}
          </cr-flex>
        </board-component-drag-drop>
      </cr-flex>
    `;
  }

  private _warning(userType: 'SchuKo' | 'LeKo' | 'OS' | 'US' | 'DiKu', user: ChancenwerkUser): CardWarning {
    switch (userType) {
      case 'SchuKo':
        return !(this.kpf.value.schuKos ?? []).find(s => s._id === user._id)
          ? { text: 'Dieser SchuKo ist nicht mehr aktiv!', tone: 'error' }
          : null;
      case 'LeKo':
        return !(this.kpf.value.leKos ?? []).find(s => s._id === user._id)
          ? { text: 'Dieser LeKo ist nicht mehr aktiv!', tone: 'error' }
          : null;
      case 'OS':
      case 'US':
        if (user instanceof Student) {
          if (!user.active) {
            return {
              text: `Dieser Schüler ist seit dem ${user.kuendigung.austrittsdatum?.toFormat(
                'dd.MM.yyyy',
              )} ausgetreten!`,
              tone: 'error',
            };
          }
          return this._deselectedDaysWarning(userType, user);
        }
        break;
      case 'DiKu':
        return null;
      default:
        throw new Error(`userType not known`);
    }
    return null;
  }

  private _deselectedDaysWarning(userType: 'OS' | 'US', student: Student): CardWarning {
    // only Lernförderung has profile day wishes; other course type pages don't even load them.
    // An empty wish list is not an early-out: a student who picked no day at all is misassigned in
    // every course they sit in, and that is exactly what we want to surface.
    const wishes = student.lernfoerderungDuerchfuehrungsTagChoosen ?? [];

    const deselectedDays = this.coursesToManage.value
      .filter(
        c =>
          c instanceof CourseLernfoerderung &&
          (userType === 'US' ? c.usStudentIDs : c.osStudentIDs).includes(student._id) &&
          !wishes.some(w => courseMatchesDurchfuehrungsTag(c, w)),
      )
      .map(c => c.weekday)
      .filter(Boolean);

    if (!deselectedDays.length) return null;
    return { text: `Im Profil abgewählt: ${deselectedDays.join(', ')}. Bitte Kurszuteilung prüfen!`, tone: 'info' };
  }

  private _getIDArrayForUserType(userType: 'SchuKo' | 'LeKo' | 'OS' | 'US' | 'DiKu'): CourseUserArrayIDs {
    switch (userType) {
      case 'SchuKo':
        return 'schuKoIDs';
      case 'LeKo':
        return 'leKoIDs';
      case 'OS':
        return 'osStudentIDs';
      case 'US':
        return 'usStudentIDs';
      case 'DiKu':
        return 'diKuIDs';
      default:
        throw new Error(`userType not known`);
    }
  }

  private _getArrayForUserType(userType: 'SchuKo' | 'LeKo' | 'OS' | 'US' | 'DiKu'): CourseUserArray {
    switch (userType) {
      case 'SchuKo':
        return 'schuKos';
      case 'LeKo':
        return 'leKos';
      case 'OS':
        return 'osStudents';
      case 'US':
        return 'usStudents';
      case 'DiKu':
        return 'diKus';
      default:
        throw new Error(`userType not known`);
    }
  }

  private _getNameUserType(userType: 'SchuKo' | 'LeKo' | 'OS' | 'US' | 'DiKu'): string {
    switch (userType) {
      case 'SchuKo':
        return 'SchuKo';
      case 'LeKo':
        return 'LeKo';
      case 'OS':
        return 'Jugendliche';
      case 'US':
        return 'Kinder';
      case 'DiKu':
        return 'digitale Kursleitung';
      default:
        throw new Error(`userType not known`);
    }
  }

  private _getMaximumPersonsPerUserType(userType: 'SchuKo' | 'LeKo' | 'OS' | 'US' | 'DiKu', course?: Course): number {
    if (course && course instanceof CourseAbschluss) {
      switch (userType) {
        case 'SchuKo':
          return 1;
        case 'LeKo':
          return 1;
        case 'OS':
          return course.maxAttendanceCount ?? 1;
        case 'US':
          return 0;
        case 'DiKu':
          return 0;
        default:
          throw new Error(`userType not known`);
      }
    }

    if (!this.kpf.value.produkt) {
      return 0;
    }

    const produkt = this.kpf.value.produkt;
    if (!(produkt instanceof ProductLKK)) {
      return 0;
    }

    switch (userType) {
      case 'SchuKo':
        return 1;
      case 'LeKo':
        return -1;
      case 'OS':
        return produkt.OSLernFoerderung ?? -1;
      case 'US':
        return produkt.USLernFoerderung ?? -1;
      case 'DiKu':
        return 1;
      default:
        throw new Error(`userType not known`);
    }
  }

  private _sortUsers(
    usUsersManaged: Array<{ user: ChancenwerkUser; deduped: boolean; fromCourseIndex: number }>,
  ): Array<{ user: ChancenwerkUser; deduped: boolean; fromCourseIndex: number }> {
    // eslint-disable-next-line complexity
    return usUsersManaged.sort((userA, userB) => {
      if (userA.deduped && !userB.deduped) return -1;
      if (!userA.deduped && userB.deduped) return 1;

      if (userA.deduped && userB.deduped) {
        return (userA.user.nachname ?? '').localeCompare(userB.user.nachname ?? '');
      }

      if (!userA.deduped && !userB.deduped) {
        if (userA.fromCourseIndex === userB.fromCourseIndex) {
          return (userA.user.nachname ?? '').localeCompare(userB.user.nachname ?? '');
        } else {
          const courseWeekdayA = this.coursesToManage.value.at(userA.fromCourseIndex);
          const courseWeekdayB = this.coursesToManage.value.at(userB.fromCourseIndex);

          return this._sortAfterWeekday(courseWeekdayA?.weekday, courseWeekdayB?.weekday);
        }
      }

      return -1;
    });
  }

  private _sortAfterWeekday(weekdayA: Weekday | undefined, weekdayB: Weekday | undefined) {
    const weekdays = Object.values(Weekday);
    return weekdays.indexOf(weekdayA ?? Weekday.MONDAY) - weekdays.indexOf(weekdayB ?? Weekday.MONDAY);
  }

  private _getDeduplicatedUsers(
    courses: Course[],
    array: CourseUserArray,
  ): Array<{ user: ChancenwerkUser; deduped: boolean; fromCourseIndex: number }> {
    const userMap = new Map<string, { user: ChancenwerkUser; deduped: boolean; fromCourseIndex: number }>();

    // Loop over courses
    for (let i = 0; i < courses.length; i++) {
      const course = courses[i];
      if (!course) {
        continue;
      }

      const users = course[array];
      // Check if a user is already in map if yes mark him as deduped. All non deduped users are in only one course of maybe multiple courses in the same room
      for (const user of users) {
        const userInfoObject = userMap.get(user._id);
        if (!userInfoObject) {
          userMap.set(user._id, { user, deduped: false, fromCourseIndex: i });
        } else {
          userInfoObject.deduped = true;
        }
      }
    }

    return Array.from(userMap.values());
  }

  private _getUserIsNotInAll(userID: string, arrayString: CourseUserArrayIDs) {
    const result = !this.coursesToManage.value.every(c => c[arrayString].includes(userID));
    return result;
  }

  private async _archiveCourse() {
    for (const courseToArchive of this.coursesToManage.value) {
      // * Special case for CourseDigital where we want to remove the linkedKPFIDs
      if (!this.deleteDigital && courseToArchive._class === CourseDigital._class) {
        const digital = await CourseDigital.getByID<CourseDigital>(courseToArchive._id)(courseSelectorDigital);
        if (digital) {
          digital.linkedKPFIDs = digital.linkedKPFIDs?.filter(id => id !== this.kpf.value._id) ?? [];
          await digital.save();
        }
      } else {
        await archiveCourse(courseToArchive);
      }

      const event = new CustomEvent('archived', { detail: { value: courseToArchive._id } });
      this.dispatchEvent(event);
    }
  }

  private _getCourseMappedByParticipants(courses: Course[], arrayString: CourseUserArrayIDs) {
    const courseMap = new Map<number, Course[]>();

    for (const course of courses) {
      const c = courseMap.get(course[arrayString]?.length ?? 0);
      if (c?.length) {
        c.push(course);
      } else {
        courseMap.set(course[arrayString]?.length ?? 0, [course]);
      }
    }

    return Array.from(courseMap.values());
  }

  private _getProgressBar(course: Course, arrayString: CourseUserArray, upperValue: number, text?: string) {
    const percentage = this._getPercentage(course[arrayString].length, upperValue === -1 ? 0 : upperValue);

    return html`
      <cr-flex horizontal crossaxis-center space="sm">
        <cr-text> ${text} </cr-text>
        <cr-progress-bar
          ${css({ maxWidth: '200px' })}
          flex
          .value=${percentage}
          tone=${this._getProgressBarTone(percentage)}
        ></cr-progress-bar>
        <cr-text>
          ${course[arrayString].filter(u => !!u || u._class !== TemporaryUser._class).length} /
          ${upperValue === -1 ? '?' : upperValue}
        </cr-text>
      </cr-flex>
    `;
  }

  private _getPercentage(actualNumber: number, neededNumber: number) {
    if (!actualNumber || !neededNumber) return 0;
    return actualNumber / neededNumber;
  }

  private _getProgressBarTone(percentage: number) {
    if (percentage <= 0.2) return 'error';
    if (percentage < 1.0) return 'warning';
    return 'success';
  }
}
